import React, { useEffect, useState } from 'react';
import { settingsApi } from '../lib/api';
import { Bot, Save, RefreshCw, MessageSquare, Bell, Key, ExternalLink, Headphones, Mail, Clock, Webhook, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import StarsWalletSection from '../components/StarsWalletSection';

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [settingWebhook, setSettingWebhook] = useState(false);

  const fetchSettings = async () => {
    try {
      const res = await settingsApi.get();
      setSettings(res.data);
    } catch (error) {
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookInfo = async () => {
    try {
      const res = await settingsApi.getWebhookInfo();
      setWebhookInfo(res.data);
    } catch (error) {
      console.error('Error fetching webhook info:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchWebhookInfo();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await settingsApi.update(settings);
      toast.success('Настройки сохранены');
      fetchWebhookInfo();
    } catch (error) {
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };

  const handleSetupWebhook = async () => {
    setSettingWebhook(true);
    try {
      const webhookUrl = `${window.location.origin.replace('3000', '8001').replace(/:\d+$/, '')}/api/webhook/telegram`;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const finalWebhookUrl = `${backendUrl}/api/webhook/telegram`;
      
      const res = await settingsApi.setupWebhook(finalWebhookUrl);
      if (res.data.success) {
        toast.success('Webhook успешно установлен');
        fetchWebhookInfo();
        fetchSettings();
      } else {
        toast.error(res.data.message || 'Ошибка установки webhook');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка установки webhook');
    } finally {
      setSettingWebhook(false);
    }
  };

  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Настройки бота</h1>
        <p className="text-zinc-500">Конфигурация Telegram бота, сообщений и поддержки</p>
      </div>

      <StarsWalletSection />

      <form onSubmit={handleSave} className="space-y-6">
        {/* Bot Token Section */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2AABEE]/10 flex items-center justify-center">
                <Bot className="w-5 h-5 text-[#2AABEE]" />
              </div>
              <div>
                <h3 className="card-title">Telegram Bot</h3>
                <p className="text-xs text-zinc-500">Настройки подключения к боту</p>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div>
              <Label htmlFor="telegram_token">Bot Token</Label>
              <Input
                id="telegram_token"
                type="password"
                value={settings?.telegram_token || ''}
                onChange={(e) => updateField('telegram_token', e.target.value)}
                placeholder={settings?.telegram_token_set ? 'Токен сохранён — введите новый, чтобы заменить' : '123456789:ABCdefGHIjklMNOpqrsTUVwxyz'}
                className="font-mono"
                data-testid="bot-token-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Получите токен у{' '}
                <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-[#2AABEE] hover:underline">
                  @BotFather
                </a>{' '}
                в Telegram
              </p>
            </div>

            <div>
              <Label htmlFor="bot_username">Username бота</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">@</span>
                <Input
                  id="bot_username"
                  value={(settings?.bot_username || '').replace(/^@/, '')}
                  onChange={(e) => updateField('bot_username', e.target.value.replace(/^@/, '').trim())}
                  placeholder="PDMRezervbot"
                  className="pl-8 font-mono"
                  data-testid="bot-username-input"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Нужен для прямых ссылок на каналы и тарифы: https://t.me/username?start=channel_…
              </p>
            </div>
            
            {/* Webhook Status */}
            <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Webhook className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm font-medium text-white">Статус Webhook</span>
                </div>
                {webhookInfo?.configured ? (
                  <span className="flex items-center gap-1 text-xs text-emerald-400">
                    <CheckCircle className="w-3 h-3" /> Подключен
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-red-400">
                    <XCircle className="w-3 h-3" /> Не настроен
                  </span>
                )}
              </div>
              
              {webhookInfo?.info?.url && (
                <p className="text-xs text-zinc-500 mb-3 font-mono break-all">
                  {webhookInfo.info.url}
                </p>
              )}
              
              <Button
                type="button"
                size="sm"
                onClick={handleSetupWebhook}
                disabled={settingWebhook || !(settings?.telegram_token_set || settings?.telegram_token)}
                className="w-full bg-[#2AABEE] hover:bg-[#229ED9]"
              >
                {settingWebhook ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Установка...
                  </>
                ) : (
                  <>
                    <Webhook className="w-4 h-4 mr-2" />
                    {webhookInfo?.configured ? 'Переустановить Webhook' : 'Установить Webhook'}
                  </>
                )}
              </Button>
              
              {!(settings?.telegram_token_set || settings?.telegram_token) && (
                <p className="text-xs text-amber-400 mt-2">
                  Сначала введите и сохраните Bot Token
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="webhook_url">Webhook URL (только для просмотра)</Label>
              <Input
                id="webhook_url"
                value={webhookInfo?.info?.url || settings?.webhook_url || ''}
                disabled
                className="font-mono opacity-70"
                data-testid="webhook-url-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                URL устанавливается автоматически при нажатии кнопки выше.
                Секрет webhook берётся из переменной TELEGRAM_WEBHOOK_SECRET на сервере и в эту форму не попадает.
              </p>
            </div>
          </div>
        </div>

        {/* CryptoBot Section */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <Key className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="card-title">CryptoBot API</h3>
                <p className="text-xs text-zinc-500">Настройки для криптоплатежей</p>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div>
              <Label htmlFor="cryptobot_token">CryptoBot API Token</Label>
              <Input
                id="cryptobot_token"
                type="password"
                value={settings?.cryptobot_token || ''}
                onChange={(e) => updateField('cryptobot_token', e.target.value)}
                placeholder={settings?.cryptobot_token_set ? 'Токен сохранён — введите новый, чтобы заменить' : 'Ваш API токен от @CryptoBot'}
                className="font-mono"
                data-testid="cryptobot-token-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Получите токен:{' '}
                <a href="https://t.me/CryptoBot" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline inline-flex items-center gap-1">
                  @CryptoBot <ExternalLink className="w-3 h-3" />
                </a>
                {' '}→ Crypto Pay → Create App
              </p>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="card-title">Техподдержка</h3>
                <p className="text-xs text-zinc-500">Контакты службы поддержки для пользователей</p>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support_username">Telegram аккаунт поддержки</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">@</span>
                  <Input
                    id="support_username"
                    value={settings?.support_username || ''}
                    onChange={(e) => updateField('support_username', e.target.value.replace('@', ''))}
                    placeholder="support_username"
                    className="pl-8"
                    data-testid="support-username-input"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-1">
                  Username аккаунта для связи с поддержкой
                </p>
              </div>
              <div>
                <Label htmlFor="support_email">Email поддержки</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="support_email"
                    type="email"
                    value={settings?.support_email || ''}
                    onChange={(e) => updateField('support_email', e.target.value)}
                    placeholder="support@example.com"
                    className="pl-10"
                    data-testid="support-email-input"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="support_working_hours">Часы работы</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="support_working_hours"
                    value={settings?.support_working_hours || ''}
                    onChange={(e) => updateField('support_working_hours', e.target.value)}
                    placeholder="Пн-Пт: 10:00 - 19:00 (МСК)"
                    className="pl-10"
                    data-testid="support-hours-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="support_response_time">Время ответа</Label>
                <Input
                  id="support_response_time"
                  value={settings?.support_response_time || ''}
                  onChange={(e) => updateField('support_response_time', e.target.value)}
                  placeholder="24 часа"
                  data-testid="support-response-time-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="support_message">Сообщение техподдержки</Label>
              <Textarea
                id="support_message"
                value={settings?.support_message || ''}
                onChange={(e) => updateField('support_message', e.target.value)}
                placeholder="Если у вас возникли вопросы или проблемы, свяжитесь с нами."
                rows={3}
                className="bg-[#121212] border-white/10"
                data-testid="support-message-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Дополнительный текст, который увидит пользователь при нажатии "Техподдержка"
              </p>
            </div>
          </div>
        </div>

        {/* Messages Section */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="card-title">Сообщения бота</h3>
                <p className="text-xs text-zinc-500">Тексты автоматических сообщений</p>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div>
              <Label htmlFor="welcome_message">Приветственное сообщение</Label>
              <Textarea
                id="welcome_message"
                value={settings?.welcome_message || ''}
                onChange={(e) => updateField('welcome_message', e.target.value)}
                placeholder="Добро пожаловать! Выберите канал для подписки."
                rows={3}
                className="bg-[#121212] border-white/10"
                data-testid="welcome-message-input"
              />
            </div>
            <div>
              <Label htmlFor="payment_success_message">Успешная оплата</Label>
              <Textarea
                id="payment_success_message"
                value={settings?.payment_success_message || ''}
                onChange={(e) => updateField('payment_success_message', e.target.value)}
                placeholder="Оплата успешно подтверждена! Ваша подписка активирована."
                rows={3}
                className="bg-[#121212] border-white/10"
                data-testid="payment-success-message-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                К этому сообщению автоматически добавляется персональная ссылка-приглашение
              </p>
            </div>
            <div>
              <Label htmlFor="payment_pending_message">Ожидание оплаты</Label>
              <Textarea
                id="payment_pending_message"
                value={settings?.payment_pending_message || ''}
                onChange={(e) => updateField('payment_pending_message', e.target.value)}
                placeholder="Ожидаем подтверждения платежа..."
                rows={3}
                className="bg-[#121212] border-white/10"
                data-testid="payment-pending-message-input"
              />
            </div>
            <div>
              <Label htmlFor="subscription_expired_message">Истечение подписки</Label>
              <Textarea
                id="subscription_expired_message"
                value={settings?.subscription_expired_message || ''}
                onChange={(e) => updateField('subscription_expired_message', e.target.value)}
                placeholder="Ваша подписка истекла. Продлите её для доступа к контенту."
                rows={3}
                className="bg-[#121212] border-white/10"
                data-testid="subscription-expired-message-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Отправляется при автоматическом удалении пользователя из канала
              </p>
            </div>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <h3 className="card-title">Уведомления и автоматизация</h3>
                <p className="text-xs text-zinc-500">Настройки автоматических действий</p>
              </div>
            </div>
          </div>
          <div className="card-content space-y-4">
            <div className="flex items-start justify-between gap-4" data-testid="auto-kick-row">
              <div>
                <Label htmlFor="auto_kick_enabled">Удалять из каналов после окончания подписки</Label>
                <p className="text-xs text-zinc-500 mt-1">
                  Если выключено, подписка просто помечается истёкшей, а человек остаётся в канале.
                  Включение действует только на подписки, которые истекут после этого момента.
                </p>
              </div>
              <Switch
                id="auto_kick_enabled"
                checked={settings?.auto_kick_enabled === true}
                onCheckedChange={(v) => updateField('auto_kick_enabled', v)}
                data-testid="auto-kick-switch"
              />
            </div>
            <div>
              <Label htmlFor="renewal_reminder_days">Напоминание о продлении (дней до истечения)</Label>
              <Input
                id="renewal_reminder_days"
                type="number"
                min="1"
                max="30"
                value={settings?.renewal_reminder_days || 3}
                onChange={(e) => updateField('renewal_reminder_days', parseInt(e.target.value))}
                className="w-32"
                data-testid="renewal-reminder-days-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                За сколько дней до истечения подписки отправить напоминание
              </p>
            </div>
            <div>
              <Label htmlFor="renewal_discount_notification_message">
                Уведомление о скидке за продление
              </Label>
              <Textarea
                id="renewal_discount_notification_message"
                value={settings?.renewal_discount_notification_message || ''}
                onChange={(e) =>
                  updateField('renewal_discount_notification_message', e.target.value)
                }
                placeholder={`⏰ Ваша подписка скоро истекает!\n\nТариф: {tariff_name}\nОсталось дней: {days_left}\n\n🎁 Скидка {discount_label} на продление!`}
                rows={5}
                className="bg-[#121212] border-white/10"
                data-testid="renewal-discount-notification-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Отправляется пользователям в окне скидки за продление. Плейсхолдеры:{' '}
                <code>{'{tariff_name}'}</code>, <code>{'{days_left}'}</code>,{' '}
                <code>{'{discount_label}'}</code>. Пусто — текст по умолчанию.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/5">
              <h4 className="text-sm font-medium text-white mb-2">Автоматические действия</h4>
              <ul className="text-xs text-zinc-400 space-y-1">
                <li>• Проверка истекших подписок — каждые 5 минут</li>
                <li>• Уведомление о скидке за продление — в том же цикле</li>
                <li>• Удаление из канала при истечении — автоматически</li>
                <li>• Уникальные invite-ссылки — создаются при активации</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end gap-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={fetchSettings}
            data-testid="reset-settings-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Сбросить
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="glow-primary"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </Button>
        </div>
      </form>
    </div>
  );
}
